import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ListTodo, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, PageHeader } from "@/components/common";
import ListForm from "@/components/lists/ListForm";
import { AMBER, Shoppers, slug } from "@/components/lists/listsUi";

function ListCard({ l, hubId, tid, i }) {
  return (
    <Link to={`/h/${hubId}/lists/${l.id}`} className="q-card group relative flex flex-col gap-3 transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 q-rise"
      style={{ animationDelay: `${i * 50}ms`, borderColor: l.shoppers.length ? `rgba(${AMBER},0.6)` : undefined, boxShadow: l.shoppers.length ? `0 0 28px rgba(${AMBER},0.18)` : undefined }}
      data-testid={`list-card-${tid}`} data-list-id={l.id}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl text-2xl" style={{ background: `rgba(${AMBER},0.12)` }} data-testid={`list-card-emoji-${tid}`}>{l.emoji}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-lg font-extrabold text-q-text" data-testid={`list-card-name-${tid}`}>{l.name}</p>
          <p className="truncate text-xs text-q-muted" data-testid={`list-card-store-${tid}`}>{l.store || "Any store"}</p>
        </div>
        {l.following && <Bell className="h-4 w-4 flex-shrink-0" style={{ color: `rgb(${AMBER})` }} data-testid={`list-card-following-${tid}`} />}
      </div>
      <div className="mt-auto flex items-end justify-between gap-2">
        <p className="font-display text-3xl font-extrabold leading-none tabular-nums text-q-text">
          <span data-testid={`list-card-left-${tid}`}>{l.left}</span> <span className="text-sm font-bold text-q-faint">left</span>
        </p>
        <Shoppers list={l.shoppers} testid={`list-card-shoppers-${tid}`} />
      </div>
    </Link>
  );
}

export default function Lists() {
  const { hub } = useHub();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/lists`).then(({ data: d }) => setData(d)).catch(() => {}), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.organization_id === hub.id && m.entity?.startsWith("list"));
  if (!data) return <Loader />;
  const seen = {};
  const tidOf = (l) => { const s = slug(l.name) || "list"; seen[s] = (seen[s] || 0) + 1; return seen[s] > 1 ? `${s}-${seen[s]}` : s; };

  return (
    <div data-testid="lists-page">
      <PageHeader eyebrow="Lists" title="Shopping lists" rgb={AMBER} testid="lists-title"
        actions={data.can_create && <button className="q-btn q-btn-primary" onClick={() => setCreating(true)} data-testid="lists-new-btn"><Plus className="h-4 w-4" /> New list</button>} />
      {data.lists.length === 0 ? (
        <EmptyState Icon={ListTodo} rgb={AMBER} testid="lists-empty" title="No lists yet" hint="Make one for Groceries, Costco or any store. Everyone sees changes live." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="lists-grid">
          {data.lists.map((l, i) => <ListCard key={l.id} l={l} hubId={hub.id} tid={tidOf(l)} i={i} />)}
        </div>
      )}
      {creating && <ListForm hubId={hub.id} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}
